import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, Subject, takeUntil } from 'rxjs';
import {
  ExternalDataService,
  Appointment,
  Zone,
  Agent,
  CreateAppointment,
} from '../../services/externalDataService.service';
import { AuthService } from '../../services/auth.service';
import { PaginationComponent } from '../shared/pagination/pagination.component';
import { UpdateAppointmentFeesComponent } from '../appointments/update-appointment-fees/update-appointment-fees.component';
import { DistrictZoneFilterComponent } from '../shared/district-zone-filter/district-zone-filter.component';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';

@Component({
  selector: 'app-appointments-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PaginationComponent,
    UpdateAppointmentFeesComponent,
    DistrictZoneFilterComponent,
  ],
  templateUrl: './appointments-list.component.html',
  styleUrl: './appointments-list.component.css',
})
export class AppointmentsListComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('mapContainer', { static: false })
  mapContainer!: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();
  private documentClickHandler: (event: Event) => void;

  // View mode toggle
  viewMode: 'grid' | 'map' = 'grid';

  // Map properties
  private map: google.maps.Map | null = null;
  private zonePolygons: Map<string, google.maps.Polygon> = new Map();
  private appointmentMarkers: Map<string, google.maps.Marker> = new Map();
  mapInitialized = false;
  showTraffic = false;
  private trafficLayer: google.maps.TrafficLayer | null = null;

  // Zone colors for visual distinction
  readonly zoneColors = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#FFA07A',
    '#98D8C8',
    '#F7DC6F',
    '#BB8FCE',
    '#85C1E2',
    '#F8B739',
    '#52B788',
  ];

  // Data properties
  appointments: Appointment[] = [];
  zones: Zone[] = [];

  // Loading and error states
  loading = false;
  errorMessage = '';

  // Pagination properties
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages: number = 1;

  // Filter properties
  filters = {
    dateFrom: '',
    dateTo: '',
    status: '',
    customerName: '',
    phoneNumber: '',
  };

  // Status options for checkbox dropdown
  statusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'Cancelled', label: 'Cancelled' },
    { value: 'Fulfilled', label: 'Fulfilled' },
    { value: 'ReScheduled', label: 'ReScheduled' },
    { value: 'Confirmed', label: 'Confirmed' },
    { value: 'Rejected', label: 'Rejected' },
    { value: 'Expired', label: 'Expired' },
  ];

  // Selected statuses (array of selected status values)
  selectedStatuses: string[] = [];

  // Selected geo zones (array of selected geozone ids)
  selectedGeoZones: string[] = [];

  // Selected agents (array of selected agent ids)
  selectedAgents: string[] = [];
  
  // All agents for filter dropdown
  allAgents: Agent[] = [];

  // Status dropdown open/close state
  statusDropdownOpen = false;

  // Agent dropdown open/close state
  agentDropdownOpen = false;

  // View appointment modal properties
  showViewAppointmentModal = false;
  selectedAppointmentForView: Appointment | null = null;

  // Update fees modal properties
  selectedAppointmentForFees: Appointment | null = null;
  showUpdateFeesModal = false;

  // Remark popup properties
  showRemarkPopup = false;
  remarkText = '';
  actionType: 'confirm' | 'reject' | 'cancel' | null = null;
  processingAction = false;
  actionError = '';

  // Reschedule modal properties
  showRescheduleModal = false;
  rescheduleDate = '';
  rescheduleTimeSlot = '';
  rescheduleRemarks = '';
  processingReschedule = false;
  rescheduleError = '';
  timeSlotOptions: string[] = [];

  // Agent assignment modal properties
  showAssignAgentModal = false;
  selectedAppointmentForAssignment: Appointment | null = null;
  agents: Agent[] = [];
  selectedAgentId: string = '';
  loadingAgents = false;
  assigningAgent = false;
  assignAgentError = '';

  // Make Math available in template
  Math = Math;

  private readonly STORAGE_KEY = 'appointments_list_state';

  constructor(
    private externalDataService: ExternalDataService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {
    // Bind the document click handler
    this.documentClickHandler = this.handleDocumentClick.bind(this);
  }

  ngOnInit(): void {
    const stateRestored = this.restoreState();

    if (!stateRestored) {
      // Set default date values
      this.filters.dateFrom = this.getDefaultFromDate();
      this.filters.dateTo = this.getDefaultToDate();
    }

    this.loadZones();
    this.loadAllAgents();
    this.loadGlobalSettings();
    this.loadAppointments();
    // Close dropdown when clicking outside
    document.addEventListener('click', this.documentClickHandler);
  }

  ngAfterViewInit(): void {
    // Map will be initialized when switching to map view
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('click', this.documentClickHandler);

    // Clean up map resources
    this.appointmentMarkers.forEach((marker) => marker.setMap(null));
    this.appointmentMarkers.clear();
    this.zonePolygons.forEach((polygon) => polygon.setMap(null));
    this.zonePolygons.clear();
    if (this.trafficLayer) {
      this.trafficLayer.setMap(null);
    }
    this.map = null;
  }

  private handleDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    
    // Status dropdown
    const statusDropdown = document.querySelector('#statusFilter')?.closest('.status-dropdown-container');
    if (statusDropdown && !statusDropdown.contains(target)) {
      this.statusDropdownOpen = false;
    }

    // Agent dropdown
    const agentDropdown = document.querySelector('#agentFilter')?.closest('.status-dropdown-container');
    if (agentDropdown && !agentDropdown.contains(target)) {
      this.agentDropdownOpen = false;
    }
  }

  private saveState(): void {
    const state = {
      filters: this.filters,
      selectedStatuses: this.selectedStatuses,
      selectedGeoZones: this.selectedGeoZones,
      selectedAgents: this.selectedAgents,
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      viewMode: this.viewMode
    };
    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
  }

  private restoreState(): boolean {
    const savedState = sessionStorage.getItem(this.STORAGE_KEY);
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        this.filters = state.filters || this.filters;
        this.selectedStatuses = state.selectedStatuses || [];
        this.selectedGeoZones = state.selectedGeoZones || [];
        this.selectedAgents = state.selectedAgents || [];
        this.currentPage = state.currentPage || 1;
        this.pageSize = state.pageSize || 10;
        this.viewMode = state.viewMode || 'grid';
        return true;
      } catch (e) {
        console.error('Error parsing saved state', e);
        return false;
      }
    }
    return false;
  }

  loadGlobalSettings(): void {
    this.externalDataService
      .getGlobalSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          if (
            settings &&
            settings.appointmentTimeSlots &&
            settings.appointmentTimeSlots.length > 0
          ) {
            this.timeSlotOptions = settings.appointmentTimeSlots;
          } else {
            // Fallback if empty
            this.timeSlotOptions = ['8 AM – 1 PM', '1 PM – 5 PM'];
          }
        },
        error: (error) => {
          console.error('Error loading global settings:', error);
          // Fallback
          this.timeSlotOptions = ['8 AM – 1 PM', '1 PM – 5 PM'];
        },
      });
  }

  loadZones(): void {
    this.externalDataService
      .getAllZones()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zones) => {
          this.zones = zones;
          // Update map if in map view
          if (this.viewMode === 'map' && this.mapInitialized) {
            this.renderZones();
            this.fitMapToBounds();
          }
        },
        error: (error) => {
          console.error('Error loading zones:', error);
        },
      });
  }

  loadAllAgents(): void {
    this.externalDataService
      .getAllAgents(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (agents) => {
          this.allAgents = agents;
        },
        error: (error) => {
          console.error('Error loading agents:', error);
        },
      });
  }

  loadAppointments(): void {
    this.loading = true;
    this.errorMessage = '';

    const pageIndex = this.currentPage - 1;

    // Build filter object
    const filterParams: any = {
      pageIndex: pageIndex,
      pageSize: this.pageSize,
    };

    if (this.filters.dateFrom) {
      filterParams.dateFrom = new Date(this.filters.dateFrom).toISOString();
    }
    if (this.filters.dateTo) {
      // Set time to end of day for dateTo
      const dateTo = new Date(this.filters.dateTo);
      dateTo.setHours(23, 59, 59, 999);
      filterParams.dateTo = dateTo.toISOString();
    }
    // Combine selected statuses into comma-separated string
    if (this.selectedStatuses.length > 0) {
      filterParams.status = this.selectedStatuses.join(',');
    }
    if (this.selectedGeoZones.length > 0) {
      filterParams.geoZoneIds = this.selectedGeoZones.join(',');
    }
    if (this.selectedAgents.length > 0) {
      filterParams.agentIds = this.selectedAgents.join(',');
    }
    if (this.filters.customerName?.trim()) {
      filterParams.customerName = this.filters.customerName.trim();
    }
    if (this.filters.phoneNumber?.trim()) {
      filterParams.phoneNumber = this.filters.phoneNumber.trim();
    }

    this.externalDataService
      .getAppointmentsWithFilter(filterParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.appointments = data.items;
          this.totalCount = data.totalCount;
          this.calculateTotalPages();
          this.loading = false;

          // Update map if in map view
          if (this.viewMode === 'map' && this.mapInitialized) {
            setTimeout(() => {
              this.updateMap();
            }, 0);
          }
        },
        error: (error) => {
          console.error('Error loading appointments:', error);

          if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage =
              'Access denied. You do not have permission to view appointments.';
          } else if (error.status === 404) {
            this.errorMessage = 'Appointments not found.';
          } else if (error.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else if (error.status === 0 || error.status === undefined) {
            this.errorMessage =
              'Network error. Please check your connection and try again.';
          } else {
            this.errorMessage =
              'Failed to load appointments. Please try again.';
          }

          this.loading = false;
        },
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.saveState();
    this.loadAppointments();
    // Map will update automatically when appointments load
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1; // Reset to first page when changing page size
    this.saveState();
    this.loadAppointments();
    // Map will update automatically when appointments load
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.saveState();
    this.loadAppointments();
    // Map will update automatically when appointments load
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.saveState();
    this.loadAppointments();
    // Map will update automatically when appointments load
  }

  clearFilters(): void {
    this.filters = {
      dateFrom: '',
      dateTo: '',
      status: '',
      customerName: '',
      phoneNumber: '',
    };
    this.selectedStatuses = [];
    this.selectedGeoZones = [];
    this.selectedAgents = [];
    this.statusDropdownOpen = false;
    this.agentDropdownOpen = false;
    this.currentPage = 1;
    sessionStorage.removeItem(this.STORAGE_KEY);
    this.loadAppointments();
  }

  hasActiveFilters(): boolean {
    return !!(
      this.filters.dateFrom ||
      this.filters.dateTo ||
      this.selectedStatuses.length > 0 ||
      this.selectedGeoZones.length > 0 ||
      this.selectedAgents.length > 0 ||
      this.filters.customerName?.trim() ||
      this.filters.phoneNumber?.trim()
    );
  }

  toggleStatus(statusValue: string): void {
    const index = this.selectedStatuses.indexOf(statusValue);
    if (index > -1) {
      this.selectedStatuses.splice(index, 1);
    } else {
      this.selectedStatuses.push(statusValue);
    }
    this.onFilterChange();
  }

  isStatusSelected(statusValue: string): boolean {
    return this.selectedStatuses.includes(statusValue);
  }

  getStatusDisplayText(): string {
    if (this.selectedStatuses.length === 0) {
      return 'All Statuses';
    }
    if (this.selectedStatuses.length === 1) {
      return (
        this.statusOptions.find((opt) => opt.value === this.selectedStatuses[0])
          ?.label || 'Selected'
      );
    }
    return `${this.selectedStatuses.length} selected`;
  }

  toggleStatusDropdown(): void {
    this.statusDropdownOpen = !this.statusDropdownOpen;
  }

  closeStatusDropdown(): void {
    this.statusDropdownOpen = false;
  }

  toggleAgent(agentId: string): void {
    const index = this.selectedAgents.indexOf(agentId);
    if (index > -1) {
      this.selectedAgents.splice(index, 1);
    } else {
      this.selectedAgents.push(agentId);
    }
    this.onFilterChange();
  }

  isAgentSelected(agentId: string): boolean {
    return this.selectedAgents.includes(agentId);
  }

  getAgentDisplayText(): string {
    if (this.selectedAgents.length === 0) {
      return 'All Agents';
    }
    if (this.selectedAgents.length === 1) {
      return (
        this.allAgents.find((a) => a.agentId === this.selectedAgents[0])
          ?.name || 'Selected'
      );
    }
    return `${this.selectedAgents.length} selected`;
  }

  toggleAgentDropdown(): void {
    this.agentDropdownOpen = !this.agentDropdownOpen;
  }

  closeAgentDropdown(): void {
    this.agentDropdownOpen = false;
  }

  private getDefaultFromDate(): string {
    const date = new Date();
    date.setDate(date.getDate());
    return date.toISOString().split('T')[0];
  }

  private getDefaultToDate(): string {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date.toISOString().split('T')[0];
  }

  private calculateTotalPages(): void {
    this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatDateOnly(dateString: string | null): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'status-pending';
      case 'cancelled':
        return 'status-cancelled';
      case 'fulfilled':
        return 'status-fulfilled';
      case 'rescheduled':
        return 'status-rescheduled';
      default:
        return 'status-unknown';
    }
  }

  isRescheduled(appointment: Appointment | null): boolean {
    return !!appointment?.parentId;
  }

  navigateToDetail(appointment: Appointment): void {
    this.router.navigate(['/appointments-list', appointment.id]);
  }

  openViewAppointmentModal(appointment: Appointment): void {
    this.selectedAppointmentForView = appointment;
    this.showViewAppointmentModal = true;
  }

  closeViewAppointmentModal(): void {
    this.showViewAppointmentModal = false;
    this.selectedAppointmentForView = null;
    // Close remark popup if it's open
    if (this.showRemarkPopup) {
      this.closeRemarkPopup();
    }
    // Close reschedule modal if it's open
    if (this.showRescheduleModal) {
      this.closeRescheduleModal();
    }
  }

  getGoogleMapsUrl(appointment: Appointment): string {
    if (!appointment.location?.latitude || !appointment.location?.longitude) {
      return '';
    }

    const lat = appointment.location.latitude;
    const lng = appointment.location.longitude;
    return `https://www.google.com/maps?q=${lat},${lng}`;
  }

  canConfirmAppointment(appointment: Appointment | null): boolean {
    return appointment?.status === 'Pending';
  }

  canCancelAppointment(appointment: Appointment | null): boolean {
    if (!appointment) return false;
    const status = appointment.status?.toLowerCase();
    return status === 'pending' || status === 'confirmed';
  }

  canRejectAppointment(appointment: Appointment | null): boolean {
    return (
      appointment?.status === 'Pending' || appointment?.status === 'Confirmed'
    );
  }

  canRescheduleAppointment(appointment: Appointment | null): boolean {
    if (!appointment) {
      return false;
    }
    const status = appointment.status?.toLowerCase();
    return (
      status === 'pending' ||
      status === 'rejected' ||
      status === 'cancelled' ||
      status === 'confirmed'
    );
  }

  canUpdateFees(appointment: Appointment | null): boolean {
    if (!appointment) {
      return false;
    }
    const status = appointment.status?.toLowerCase();
    return (
      status !== 'expired' &&
      status !== 'fulfilled' &&
      status !== 'rescheduled' &&
      status !== 'cancelled'
    );
  }

  canAssignAgent(appointment: Appointment | null): boolean {
    if (!appointment) {
      return false;
    }
    const status = appointment.status?.toLowerCase();
    return (
      status !== 'expired' &&
      status !== 'fulfilled' &&
      status !== 'rescheduled' &&
      status !== 'cancelled'
    );
  }

  openRemarkPopup(type: 'confirm' | 'reject' | 'cancel'): void {
    this.actionType = type;
    this.remarkText = '';
    this.actionError = '';
    this.showRemarkPopup = true;
  }

  closeRemarkPopup(): void {
    this.showRemarkPopup = false;
    this.remarkText = '';
    this.actionType = null;
    this.actionError = '';
  }

  submitRemark(): void {
    if (
      !this.remarkText.trim() ||
      !this.selectedAppointmentForView ||
      !this.actionType
    ) {
      return;
    }

    this.processingAction = true;
    this.actionError = '';

    const appointmentId = this.selectedAppointmentForView.id;
    const remark = this.remarkText.trim();

    let apiCall: Observable<any>;

    if (this.actionType === 'confirm') {
      apiCall = this.externalDataService.confirmAppointment(
        appointmentId,
        remark,
      );
    } else if (this.actionType === 'reject') {
      apiCall = this.externalDataService.rejectAppointment(
        appointmentId,
        remark,
      );
    } else {
      apiCall = this.externalDataService.cancelAppointment(
        appointmentId,
        remark,
      );
    }

    apiCall.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.processingAction = false;
        this.closeRemarkPopup();
        this.closeViewAppointmentModal();
        // Reload appointments to reflect the change
        this.loadAppointments();
      },
      error: (error) => {
        console.error(`Failed to ${this.actionType} appointment:`, error);
        this.processingAction = false;

        if (error.status === 401) {
          this.actionError = 'Authentication required. Please log in again.';
        } else if (error.status === 403) {
          this.actionError =
            'Access denied. You do not have permission to perform this action.';
        } else if (error.status === 404) {
          this.actionError = 'Appointment not found.';
        } else if (error.status >= 500) {
          this.actionError = 'Server error. Please try again later.';
        } else {
          this.actionError = `Failed to ${this.actionType} appointment. Please try again.`;
        }
      },
    });
  }

  openRescheduleModal(): void {
    if (!this.selectedAppointmentForView) {
      return;
    }

    // Prevent rescheduling if appointment is already rescheduled
    if (!this.canRescheduleAppointment(this.selectedAppointmentForView)) {
      return;
    }

    // Pre-fill with current appointment values
    const appointmentDate = new Date(this.selectedAppointmentForView.date);
    this.rescheduleDate = appointmentDate.toISOString().split('T')[0];
    this.rescheduleTimeSlot = this.selectedAppointmentForView.timeSlot || '';

    // Get the most recent remark text if it exists from history
    const history = this.selectedAppointmentForView.remarkHistory;
    this.rescheduleRemarks =
      history && history.length > 0 ? history[history.length - 1].text : '';

    this.rescheduleError = '';
    this.showRescheduleModal = true;
  }

  closeRescheduleModal(): void {
    this.showRescheduleModal = false;
    this.rescheduleDate = '';
    this.rescheduleTimeSlot = '';
    this.rescheduleRemarks = '';
    this.rescheduleError = '';
    this.processingReschedule = false;
  }

  getCurrentDate(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  submitReschedule(): void {
    if (
      !this.selectedAppointmentForView ||
      !this.rescheduleDate ||
      !this.rescheduleTimeSlot
    ) {
      this.rescheduleError =
        'Please fill in all required fields (Date and Time Slot).';
      return;
    }

    this.processingReschedule = true;
    this.rescheduleError = '';
    // Create updated appointment object with new date, time slot, and remarks
    const updatedAppointment: CreateAppointment = {
      customerId: this.selectedAppointmentForView.customerId,
      date: new Date(this.rescheduleDate).toISOString(),
      address: this.selectedAppointmentForView.address,
      addressId: this.selectedAppointmentForView.addressId,
      location: this.selectedAppointmentForView.location,
      locationName: this.selectedAppointmentForView.locationName,
      geoZoneId: this.selectedAppointmentForView.geoZoneId,
      pinCode: this.selectedAppointmentForView.pinCode,
      timeSlot: this.rescheduleTimeSlot,
      remarks: this.rescheduleRemarks.trim() || undefined,
      appointmentId: this.selectedAppointmentForView.id,
    };
    this.externalDataService
      .rescheduleAppointment(updatedAppointment)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.processingReschedule = false;
          this.closeRescheduleModal();
          this.closeViewAppointmentModal();
          // Reload appointments to reflect the change
          this.loadAppointments();
        },
        error: (error) => {
          console.error('Failed to reschedule appointment:', error);
          this.processingReschedule = false;

          if (error.status === 401) {
            this.rescheduleError =
              'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.rescheduleError =
              'Access denied. You do not have permission to reschedule appointments.';
          } else if (error.status === 404) {
            this.rescheduleError = 'Appointment not found.';
          } else if (error.status >= 500) {
            this.rescheduleError = 'Server error. Please try again later.';
          } else {
            this.rescheduleError =
              error.error?.message ||
              'Failed to reschedule appointment. Please try again.';
          }
        },
      });
  }

  openAssignAgentModal(appointment: Appointment): void {
    this.selectedAppointmentForAssignment = appointment;
    this.selectedAgentId = appointment.agentId || '';
    this.showAssignAgentModal = true;
    this.assignAgentError = '';
    this.loadAgents();
  }

  closeAssignAgentModal(): void {
    this.showAssignAgentModal = false;
    this.selectedAppointmentForAssignment = null;
    this.selectedAgentId = '';
    this.agents = [];
    this.assignAgentError = '';
  }

  loadAgents(): void {
    this.loadingAgents = true;
    this.externalDataService
      .getActiveAgents()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (agents) => {
          this.agents = agents;
          this.loadingAgents = false;
        },
        error: (error) => {
          console.error('Error loading agents:', error);
          this.assignAgentError = 'Failed to load agents. Please try again.';
          this.loadingAgents = false;
        },
      });
  }

  assignAgent(): void {
    if (!this.selectedAppointmentForAssignment || !this.selectedAgentId) {
      this.assignAgentError = 'Please select an agent.';
      return;
    }

    this.assigningAgent = true;
    this.assignAgentError = '';

    this.externalDataService
      .assignAgentToAppointment(
        this.selectedAppointmentForAssignment.id,
        this.selectedAgentId,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.assigningAgent = false;
          this.closeAssignAgentModal();
          // Reload appointments to reflect the change
          this.loadAppointments();
        },
        error: (error) => {
          console.error('Error assigning agent:', error);
          this.assigningAgent = false;

          if (error.status === 401) {
            this.assignAgentError =
              'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.assignAgentError =
              'Access denied. You do not have permission to assign agents.';
          } else if (error.status === 404) {
            this.assignAgentError = 'Appointment not found.';
          } else if (error.status >= 500) {
            this.assignAgentError = 'Server error. Please try again later.';
          } else {
            this.assignAgentError = 'Failed to assign agent. Please try again.';
          }
        },
      });
  }

  onUpdateFees(appointment: Appointment): void {
    this.selectedAppointmentForFees = appointment;
    this.showUpdateFeesModal = true;
  }

  onFeesUpdated(): void {
    this.showUpdateFeesModal = false;
    this.selectedAppointmentForFees = null;
    this.loadAppointments();
  }

  onCloseFeesModal(): void {
    this.showUpdateFeesModal = false;
    this.selectedAppointmentForFees = null;
  }

  // View mode toggle
  toggleViewMode(): void {
    if (this.viewMode === 'map' && !this.mapInitialized) {
      this.initializeMap();
    } else if (this.viewMode === 'map' && this.mapInitialized) {
      // Update map when switching back to map view
      this.updateMap();
    }
  }

  // Switch to grid view
  switchToGridView(): void {
    this.viewMode = 'grid';
    this.saveState();
  }

  // Switch to map view
  switchToMapView(): void {
    this.viewMode = 'map';
    this.saveState();
    // Wait for Angular to update the view before initializing map
    this.cdr.detectChanges();
    // Use requestAnimationFrame to ensure DOM is updated
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (this.viewMode === 'map') {
          if (!this.mapInitialized) {
            this.initializeMap();
          } else {
            // Map is already initialized, but ensure it's visible and updated
            if (
              this.map &&
              this.mapContainer &&
              this.mapContainer.nativeElement
            ) {
              // Trigger map resize to ensure it renders properly
              google.maps.event.trigger(this.map, 'resize');
              // Ensure map is visible and updated
              this.updateMap();
            } else {
              // Map container might have been lost, reinitialize
              this.mapInitialized = false;
              this.map = null;
              this.initializeMap();
            }
          }
        }
      }, 150);
    });
  }

  // Map initialization
  private async initializeMap(): Promise<void> {
    if (this.mapInitialized) {
      return;
    }

    // Wait for map container to be available
    let retries = 0;
    const maxRetries = 20;

    while (
      (!this.mapContainer || !this.mapContainer.nativeElement) &&
      retries < maxRetries
    ) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      retries++;
      // Force change detection
      this.cdr.detectChanges();
    }

    if (!this.mapContainer || !this.mapContainer.nativeElement) {
      console.error(
        'Map container not found after retries. Element:',
        this.mapContainer,
      );
      // Try to find it manually as fallback
      const mapElement = document.querySelector(
        '.map-container',
      ) as HTMLElement;
      if (mapElement) {
        // Create a temporary ElementRef-like object
        this.mapContainer = {
          nativeElement: mapElement,
        } as ElementRef<HTMLDivElement>;
      } else {
        console.error('Map container element not found in DOM');
        return;
      }
    }

    try {
      await this.loadGoogleMapsApi(environment.googleMapsApiKey);
      this.createMap();
      this.mapInitialized = true;
      this.renderZones();
      this.renderAppointments();
      this.fitMapToBounds();
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  }

  private loadGoogleMapsApi(apiKey: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).google?.maps) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error('Failed to load Google Maps JS API'));
      document.head.appendChild(script);
    });
  }

  private createMap(): void {
    if (!this.mapContainer || !this.mapContainer.nativeElement) {
      console.error('Map container element not available');
      return;
    }

    // Center on Kerala, India (default location)
    this.map = new google.maps.Map(this.mapContainer.nativeElement, {
      center: { lat: 8.5241, lng: 76.9366 }, // Trivandrum, Kerala
      zoom: 10,
      mapTypeId: 'roadmap',
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: google.maps.ControlPosition.TOP_RIGHT,
      },
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_CENTER,
      },
      streetViewControl: true,
      streetViewControlOptions: {
        position: google.maps.ControlPosition.RIGHT_CENTER,
      },
      fullscreenControl: true,
      fullscreenControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP,
      },
      scaleControl: true,
    });

    // Initialize traffic layer
    this.trafficLayer = new google.maps.TrafficLayer();
    if (this.showTraffic) {
      this.trafficLayer.setMap(this.map);
    }
  }

  private renderZones(): void {
    if (!this.map) return;

    // Clear existing polygons
    this.zonePolygons.forEach((polygon) => polygon.setMap(null));
    this.zonePolygons.clear();

    this.zones.forEach((zone, index) => {
      if (
        !zone.coordinate?.coordinates ||
        zone.coordinate.coordinates.length === 0
      ) {
        return;
      }

      // Convert coordinates to Google Maps format
      const paths = zone.coordinate.coordinates.map((coord) => ({
        lat: coord.latitude,
        lng: coord.longitude,
      }));

      // Create polygon
      const polygon = new google.maps.Polygon({
        paths: paths,
        strokeColor: this.zoneColors[index % this.zoneColors.length],
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: this.zoneColors[index % this.zoneColors.length],
        fillOpacity: 0.25,
        map: this.map,
        clickable: true,
        draggable: false,
        editable: false,
      });

      // Create custom tooltip element
      const tooltip = document.createElement('div');
      tooltip.className = 'zone-tooltip';
      tooltip.textContent = zone.name;
      tooltip.style.cssText = `
        position: fixed;
        background: rgba(0, 0, 0, 0.85);
        color: white;
        padding: 6px 12px;
        border-radius: 4px;
        font-size: 13px;
        font-weight: 500;
        pointer-events: none;
        z-index: 1000;
        white-space: nowrap;
        display: none;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      `;
      document.body.appendChild(tooltip);

      // Add hover listeners to show zone name tooltip
      polygon.addListener('mouseover', (event: google.maps.MapMouseEvent) => {
        if (event.latLng && event.domEvent) {
          const mouseEvent = event.domEvent as MouseEvent;
          // Position tooltip near mouse cursor
          tooltip.style.left = mouseEvent.clientX + 10 + 'px';
          tooltip.style.top = mouseEvent.clientY - 30 + 'px';
          tooltip.style.display = 'block';
        }
      });

      polygon.addListener('mousemove', (event: google.maps.MapMouseEvent) => {
        if (event.latLng && event.domEvent) {
          const mouseEvent = event.domEvent as MouseEvent;
          // Update tooltip position as mouse moves
          tooltip.style.left = mouseEvent.clientX + 10 + 'px';
          tooltip.style.top = mouseEvent.clientY - 30 + 'px';
        }
      });

      polygon.addListener('mouseout', () => {
        tooltip.style.display = 'none';
      });

      // Clean up tooltip when polygon is removed
      const originalSetMap = polygon.setMap.bind(polygon);
      polygon.setMap = (map: google.maps.Map | null) => {
        originalSetMap(map);
        if (!map && tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip);
        }
      };

      this.zonePolygons.set(zone.id, polygon);
    });
  }

  private renderAppointments(): void {
    if (!this.map) return;

    // Clear existing markers
    this.appointmentMarkers.forEach((marker) => marker.setMap(null));
    this.appointmentMarkers.clear();

    // Use paginated appointments (same as grid view)
    const filteredAppointments = this.appointments;

    // Group appointments by coordinates (with tolerance for slight differences)
    const coordinateGroups =
      this.groupAppointmentsByCoordinates(filteredAppointments);

    coordinateGroups.forEach((appointments, key) => {
      const firstAppointment = appointments[0];
      const position = {
        lat: firstAppointment.location!.latitude,
        lng: firstAppointment.location!.longitude,
      };

      if (appointments.length === 1) {
        // Single appointment - create simple marker
        this.createSingleAppointmentMarker(firstAppointment, position);
      } else {
        // Multiple appointments at same location - create grouped marker
        this.createGroupedAppointmentMarker(appointments, position);
      }
    });
  }

  private groupAppointmentsByCoordinates(
    appointments: Appointment[],
  ): Map<string, Appointment[]> {
    const groups = new Map<string, Appointment[]>();
    const tolerance = 0.0001; // Approximately 10 meters

    appointments.forEach((appointment) => {
      if (!appointment.location?.latitude || !appointment.location?.longitude) {
        return;
      }

      // Find existing group within tolerance
      let foundGroup = false;
      for (const [key, group] of groups.entries()) {
        const [lat, lng] = key.split(',').map(Number);
        const distance = Math.sqrt(
          Math.pow(appointment.location!.latitude - lat, 2) +
            Math.pow(appointment.location!.longitude - lng, 2),
        );

        if (distance < tolerance) {
          group.push(appointment);
          foundGroup = true;
          break;
        }
      }

      // Create new group if no matching group found
      if (!foundGroup) {
        const key = `${appointment.location.latitude},${appointment.location.longitude}`;
        groups.set(key, [appointment]);
      }
    });

    return groups;
  }

  private createSingleAppointmentMarker(
    appointment: Appointment,
    position: google.maps.LatLngLiteral,
  ): void {
    if (!this.map) return;

    // Create marker
    const marker = new google.maps.Marker({
      position: position,
      map: this.map,
      title: appointment.customerName || 'Appointment',
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: this.getStatusColor(
          appointment.status,
          !!appointment.parentId,
        ),
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      },
    });

    // Add click listener to open view modal
    marker.addListener('click', () => {
      this.openViewAppointmentModal(appointment);
    });

    // Add info window on hover
    const infoWindow = new google.maps.InfoWindow({
      content: this.createAppointmentInfoContent(appointment),
    });

    marker.addListener('mouseover', () => {
      infoWindow.open(this.map!, marker);
    });

    marker.addListener('mouseout', () => {
      infoWindow.close();
    });

    this.appointmentMarkers.set(appointment.id, marker);
  }

  private createGroupedAppointmentMarker(
    appointments: Appointment[],
    position: google.maps.LatLngLiteral,
  ): void {
    if (!this.map) return;

    // Create custom icon with count badge
    const count = appointments.length;
    const svgIcon = this.createGroupedMarkerIcon(count);

    // Create marker
    const marker = new google.maps.Marker({
      position: position,
      map: this.map,
      title: `${count} appointments at this location`,
      icon: {
        url: svgIcon,
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(20, 20),
      },
    });

    // Create info window with list of appointments
    const infoWindow = new google.maps.InfoWindow({
      content: this.createGroupedAppointmentsInfoContent(appointments),
      maxWidth: 350,
    });

    // Store appointments for this marker
    const appointmentsMap = new Map<string, Appointment>();
    appointments.forEach((apt) => {
      appointmentsMap.set(apt.id, apt);
    });

    // Add click listener to show info window with all appointments
    marker.addListener('click', () => {
      infoWindow.open(this.map!, marker);

      // Add click handlers to appointment items after info window opens
      setTimeout(() => {
        const appointmentItems = document.querySelectorAll('.appointment-item');
        appointmentItems.forEach((item) => {
          const appointmentId = item.getAttribute('data-appointment-id');
          if (appointmentId && appointmentsMap.has(appointmentId)) {
            item.addEventListener('click', (e) => {
              e.stopPropagation();
              const appointment = appointmentsMap.get(appointmentId);
              if (appointment) {
                infoWindow.close();
                this.openViewAppointmentModal(appointment);
              }
            });
          }
        });
      }, 100);
    });

    // Store marker with first appointment ID for tracking
    this.appointmentMarkers.set(appointments[0].id, marker);
  }

  private createGroupedMarkerIcon(count: number): string {
    // Create SVG icon with count badge
    const svg = `
      <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="18" fill="#667eea" stroke="#ffffff" stroke-width="2"/>
        <text x="20" y="26" font-family="Arial, sans-serif" font-size="14" font-weight="bold" fill="white" text-anchor="middle">${count}</text>
      </svg>
    `;
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
  }

  private createAppointmentInfoContent(appointment: Appointment): string {
    return `
      <div style="padding: 10px; min-width: 200px;">
        <h3 style="margin: 0 0 8px 0; color: #1a73e8; font-size: 16px; font-weight: 600;">
          ${appointment.customerName || 'N/A'}
          ${appointment.parentId ? '<span style="font-size: 10px; background: #e3f2fd; color: #1976d2; padding: 2px 6px; border-radius: 4px; margin-left: 6px; border: 1px solid #bbdefb;">Rescheduled</span>' : ''}
        </h3>
        <div style="font-size: 13px; color: #5f6368; line-height: 1.6;">
          <p style="margin: 4px 0;"><strong>Date:</strong> ${this.formatDateOnly(appointment.date)}</p>
          <p style="margin: 4px 0;"><strong>Status:</strong> ${appointment.status || 'N/A'}</p>
          <p style="margin: 4px 0;"><strong>Zone:</strong> ${appointment.geoZoneName || 'N/A'}</p>
          <p style="margin: 4px 0;"><strong>Time Slot:</strong> ${appointment.timeSlot || 'N/A'}</p>
        </div>
      </div>
    `;
  }

  private createGroupedAppointmentsInfoContent(
    appointments: Appointment[],
  ): string {
    const appointmentsList = appointments
      .map((appointment, index) => {
        const statusColor = this.getStatusColor(
          appointment.status,
          !!appointment.parentId,
        );
        const rescheduledTag = appointment.parentId
          ? '<span style="font-size: 9px; background: #e3f2fd; color: #1976d2; padding: 1px 4px; border-radius: 3px; border: 1px solid #bbdefb; margin-left: 4px;">Rescheduled</span>'
          : '';
        const escapedName = (appointment.customerName || 'N/A')
          .replace(/'/g, "\\'")
          .replace(/"/g, '&quot;');
        return `
        <div class="appointment-item" 
             data-appointment-id="${appointment.id}"
             style="padding: 10px; border-bottom: 1px solid #e0e0e0; cursor: pointer; transition: background 0.2s;" 
             onmouseover="this.style.background='#f5f5f5'" 
             onmouseout="this.style.background='white'">
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
            <div style="width: 12px; height: 12px; border-radius: 50%; background-color: ${statusColor};"></div>
            <h4 style="margin: 0; color: #1a73e8; font-size: 14px; font-weight: 600;">${escapedName}${rescheduledTag}</h4>
          </div>
          <div style="font-size: 12px; color: #5f6368; margin-left: 20px;">
            <p style="margin: 2px 0;"><strong>Date:</strong> ${this.formatDateOnly(appointment.date)}</p>
            <p style="margin: 2px 0;"><strong>Status:</strong> ${appointment.status || 'N/A'}</p>
            <p style="margin: 2px 0;"><strong>Time Slot:</strong> ${appointment.timeSlot || 'N/A'}</p>
          </div>
        </div>
      `;
      })
      .join('');

    return `
      <div style="padding: 8px; max-width: 350px; max-height: 400px; overflow-y: auto;">
        <h3 style="margin: 0 0 12px 0; color: #1a73e8; font-size: 16px; font-weight: 600;">
          ${appointments.length} Appointment${appointments.length > 1 ? 's' : ''} at this location
        </h3>
        <div style="max-height: 350px; overflow-y: auto;">
          ${appointmentsList}
        </div>
        <p style="margin: 8px 0 0 0; font-size: 11px; color: #999; text-align: center;">
          Click on an appointment to view details
        </p>
      </div>
    `;
  }

  private getStatusColor(
    status: string,
    isRescheduled: boolean = false,
  ): string {
    // If it's a rescheduled appointment, always show as bright blue
    if (isRescheduled) {
      return '#00b0ff';
    }

    // Match the colors from CSS status badges (using text colors for better visibility)
    switch (status?.toLowerCase()) {
      case 'pending':
        return '#856404'; // Matches status-pending text color
      case 'confirmed':
        return '#155724'; // Green (similar to fulfilled)
      case 'fulfilled':
        return '#155724'; // Matches status-fulfilled text color
      case 'cancelled':
        return '#721c24'; // Matches status-cancelled text color
      case 'rejected':
        return '#721c24'; // Red (similar to cancelled)
      case 'rescheduled':
        return '#00b0ff'; // Bright blue for better visibility on map
      case 'expired':
        return '#383d41'; // Dark gray
      default:
        return '#383d41'; // Matches status-unknown text color
    }
  }

  private fitMapToBounds(): void {
    if (!this.map) return;

    const bounds = new google.maps.LatLngBounds();
    let hasBounds = false;

    // Use paginated appointments (same as grid view)
    const filteredAppointments = this.appointments;

    // Add appointment markers to bounds
    filteredAppointments.forEach((appointment) => {
      if (appointment.location?.latitude && appointment.location?.longitude) {
        bounds.extend({
          lat: appointment.location.latitude,
          lng: appointment.location.longitude,
        });
        hasBounds = true;
      }
    });

    // Add zone coordinates to bounds
    this.zones.forEach((zone) => {
      if (zone.coordinate?.coordinates) {
        zone.coordinate.coordinates.forEach((coord) => {
          bounds.extend({ lat: coord.latitude, lng: coord.longitude });
          hasBounds = true;
        });
      }
    });

    if (hasBounds) {
      this.map.fitBounds(bounds);
      // Limit zoom level
      const listener = google.maps.event.addListener(this.map, 'idle', () => {
        const zoom = this.map!.getZoom();
        if (zoom && zoom > 14) {
          this.map!.setZoom(14);
        }
        google.maps.event.removeListener(listener);
      });
    }
  }

  private updateMap(): void {
    if (!this.mapInitialized) {
      // If map is not initialized but we're in map view, try to initialize it
      if (this.viewMode === 'map') {
        this.initializeMap();
      }
      return;
    }

    if (!this.map) {
      console.error(
        'Map instance is null but mapInitialized is true. Reinitializing...',
      );
      this.mapInitialized = false;
      if (this.viewMode === 'map') {
        this.initializeMap();
      }
      return;
    }

    // Ensure map container is still available
    if (!this.mapContainer || !this.mapContainer.nativeElement) {
      setTimeout(() => {
        if (
          this.mapContainer &&
          this.mapContainer.nativeElement &&
          this.viewMode === 'map'
        ) {
          this.updateMap();
        } else if (this.viewMode === 'map') {
          // Container still not available, reinitialize
          this.mapInitialized = false;
          this.map = null;
          this.initializeMap();
        }
      }, 100);
      return;
    }

    // Ensure map is attached to the container
    if (this.map.getDiv() !== this.mapContainer.nativeElement) {
      // Map div doesn't match container, need to reinitialize
      this.mapInitialized = false;
      this.map = null;
      if (this.viewMode === 'map') {
        this.initializeMap();
      }
      return;
    }

    this.renderZones();
    this.renderAppointments();
    this.fitMapToBounds();
  }

  // Toggle traffic layer
  toggleTraffic(): void {
    this.showTraffic = !this.showTraffic;
    if (this.trafficLayer) {
      this.trafficLayer.setMap(this.showTraffic ? this.map : null);
    }
  }
}
